import { useState } from "react";
import { tv } from "tailwind-variants";
import type { WorkflowStepInstanceOutputAttachment } from "@/types";
import { RiArrowDownSLine, RiArrowUpSLine } from "@remixicon/react";
import { Badge } from "@bdo/cornerstone-ui";

const styles = tv({
  slots: {
    root: "bg-mono-lightest p-sm rounded flex flex-col mb-md",
    button:
      "flex w-full items-center justify-between text-mono-dark focus:outline-none focus-visible:ring-2 focus-visible:ring-info-lighter focus-visible:ring-offset-2 focus-visible:ring-offset-mono-lightest",
    feedbackText: "line-clamp-1",
  },
  variants: {
    isExpanded: {
      true: {
        feedbackText: "line-clamp-none",
      },
    },
  },
});

interface OutputFeedbackViewerProps {
  attachments: WorkflowStepInstanceOutputAttachment[];
  testId: string;
  feedbackText?: string | null;
}

export function OutputFeedbackViewer({
  attachments,
  testId,
  feedbackText,
}: Readonly<OutputFeedbackViewerProps>) {
  const trimmedFeedbackText = feedbackText?.trim();
  const hasFeedbackText = Boolean(trimmedFeedbackText);
  const hasAttachments = attachments.length > 0;

  const [isExpanded, setIsExpanded] = useState(false);

  if (!hasAttachments && !hasFeedbackText) {
    return null;
  }

  const { root, button, feedbackText: feedbackTextStyle } = styles();

  return (
    <div data-testid={testId} className={root()}>
      <button className={button()} onClick={() => setIsExpanded(!isExpanded)}>
        <span className="text-sm">Replied</span>
        {isExpanded ? <RiArrowUpSLine /> : <RiArrowDownSLine />}
      </button>
      {hasFeedbackText && (
        <div className={feedbackTextStyle({ isExpanded })}>
          {trimmedFeedbackText}
        </div>
      )}

      {isExpanded && (
        <div className="mt-md flex flex-wrap gap-sm">
          {attachments.map((attachment) => (
            <Badge key={attachment.id} variant="success">
              {attachment.fileName}
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}
